import React, { ChangeEvent, useState, useEffect } from "react";
import DashboardLayout from "../../../Layout/DashboardLayout";
import Scaffold from "../../../Components/Scaffold";
import DashboardHeader from "../../../Components/DashboardHeader";
import { useParams } from "react-router-dom";
import { useGetAdminsQuery } from "../../../hooks/AdminManagement/useGetAdminsQuery";
import Loader from "../../../Components/Loader";
import { useSuspendAdminMutation } from "../../../hooks/AdminManagement/useSuspendAdminMutation";
import { Modal, Table } from "antd";
import Input from "../../../Components/Input";
import { useGetRolesQuery } from "../../../hooks/AdminManagement/useGetRolesQuery";
import Button from "../../../Components/Button";
import { message } from "antd";
import { queryClient } from "../../../services/queryClient";
import { useUpdateAdminMutation } from "../../../hooks/AdminManagement/useUpdateAdminMutation";
import { useGetDriverOrdersonAdmin } from "hooks/OrderManagement/useGetDriverOrdersonAdmin";

const SingleAdminPreview = () => {
  const { id } = useParams();
  const { data, isPending } = useGetAdminsQuery({ id });
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);

  const initialState = {
    firstName: data?.data?.firstName || "",
    lastName: data?.data?.lastName || "",
    role: data?.data?.role?.id || "",
  };

  const [payload, setPayload] = useState(initialState);

  // Update payload when data changes
  useEffect(() => {
    if (data) {
      setPayload({
        firstName: data?.data?.firstName || "",
        lastName: data?.data?.lastName || "",
        role: data?.data?.role?.id || "",
      });
    }
  }, [data]);

  const { mutateAsync } = useSuspendAdminMutation(`${id}`);
  const { data: driverOrders, isPending: isPendingDriverOrders } =
    useGetDriverOrdersonAdmin({
      id: id || "",
    });
  const { mutateAsync: updateAdmin, isPending: updatingAdmin } =
    useUpdateAdminMutation(`${id}`);
  const { data: roles } = useGetRolesQuery();

  // Handle input changes
  const handleInputChange = (
    e: ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setPayload((prev) => ({ ...prev, [name]: value }));
  };

  // Handle modal close and reset the form
  const handleModalClose = () => {
    setIsEditModalOpen(false);
    setPayload(initialState); // Reset the payload when the modal is closed
  };

  const orderColumns = [
    {
      title: "Order No",
      dataIndex: "orderNo",
      key: "orderNo",
    },
    {
      title: "Customer",
      key: "customer",
      render: (record: any) =>
        `${record.customer?.firstName ?? ""} ${
          record.customer?.lastName ?? ""
        }`,
    },
    {
      title: "Business Name",
      key: "businessDetails",
      render: (record: any) => (
        <div>
          <p className="font-medium text-white">
            {record?.customer?.businessName}
          </p>
          <p className="text-gray-400 text-sm">{record?.customerAddress}</p>
        </div>
      ),
    },
    {
      title: "Order Status",
      dataIndex: "orderStatus",
      key: "orderStatus",
      render: (status: string) => {
        const getStatusColor = (status: string) => {
          switch (status) {
            case "delivered":
              return "text-green-500 bg-green-500/10";
            case "missed":
              return "text-red-500 bg-red-500/10";
            case "shipped":
              return "text-yellow-400 bg-yellow-400/10";
            default:
              return "text-gray-400 bg-gray-600/10";
          }
        };

        return (
          <span
            className={`px-3 py-1 rounded-full text-xs font-medium capitalize ${getStatusColor(
              status
            )}`}
          >
            {status}
          </span>
        );
      },
    },
    {
      title: "Total Amount",
      dataIndex: "totalProductsAmount",
      key: "totalProductsAmount",
      render: (amount: number) => `₦${(amount / 100).toLocaleString()}`,
    },
    {
      title: "Product Count",
      key: "orderProductsCount",
      render: (record: any) => record.orderProducts?.length ?? 0,
    },
    {
      title: "Created At",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (value: string) =>
        new Date(value).toLocaleDateString("en-NG", {
          year: "numeric",
          month: "short",
          day: "numeric",
        }),
    },
  ];

  return (
    <DashboardLayout title="Admin Management" back={false}>
      <div className="pt-[15px] pl-[15px] pr-[40px]">
        <DashboardHeader
          firstTitle={"Admin Management"}
          secondTitle={"User Details..."}
        >
          <button
            onClick={() => {
              mutateAsync(payload);
            }}
            className="bg-[#F3F3F3] rounded-md p-3 text-[12px]"
          >
            {data?.data?.isSuspended === true ? "Unsuspend" : "Suspend"} User
          </button>
        </DashboardHeader>
        {isPending && <Loader />}
        {!isPending && data && data?.data && (
          <div className="flex gap-2 pt-[15px] pl-[15px] pr-[40px]">
            {/* User Information Section */}
            <div className="w-6/12 mt-[20px] mb-[29px] border-r-[1px] border-[#eee] pr-[30px] mr-[30px]">
              <div className="flex gap-3 mb-3">
                <img
                  src="https://via.placeholder.com/500"
                  alt=""
                  className="h-[55px] w-[55px] rounded-full"
                />
                <div>
                  <h3 className="flex">
                    <span className="text-black font-bold">
                      {data?.data?.firstName + " " + data?.data?.lastName}
                    </span>
                    <span className="ml-3 px-[4px] py-[2px] bg-green-200 rounded-md text-green-600 text-[13px]">
                      Available
                    </span>
                  </h3>
                  <p className="text-[#a8a8a8] text-[15px]">
                    ID: #{data?.data?.id}
                  </p>
                </div>
              </div>
              <div className="px-[10px]">
                <Scaffold
                  left="User information"
                  right={
                    <button
                      onClick={() => setIsEditModalOpen(true)}
                      className="text-orange flex gap-2 items-center"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        strokeWidth={1.5}
                        stroke="currentColor"
                        className="size-6"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="m16.862 4.487 1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L10.582 16.07a4.5 4.5 0 0 1-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 0 1 1.13-1.897l8.932-8.931Zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0 1 15.75 21H5.25A2.25 2.25 0 0 1 3 18.75V8.25A2.25 2.25 0 0 1 5.25 6H10"
                        />
                      </svg>
                      Edit Information
                    </button>
                  }
                >
                  <div className="flex mb-4">
                    <div className="w-5/12 text-[#a8a8a8]">First Name</div>
                    <div className="w-7/12">{data?.data?.firstName}</div>
                  </div>
                  <div className="flex mb-4">
                    <div className="w-5/12 text-[#a8a8a8]">Last Name</div>
                    <div className="w-7/12">{data?.data?.lastName}</div>
                  </div>
                  <div className="flex mb-4">
                    <div className="w-5/12 text-[#a8a8a8]">Email</div>
                    <div className="w-7/12">{data?.data?.email}</div>
                  </div>
                  <div className="flex mb-4">
                    <div className="w-5/12 text-[#a8a8a8]">Role</div>
                    <div className="w-7/12">{data?.data?.role?.name}</div>
                  </div>
                  <div className="flex mb-4">
                    <div className="w-5/12 text-[#a8a8a8]">Phone Number</div>
                    <div className="w-7/12">{data?.data?.phoneNumber}</div>
                  </div>
                </Scaffold>
              </div>
            </div>
            <div className="w-6/12">
              {/* Modal Section */}
              <Modal
                title="Edit User Information"
                open={isEditModalOpen}
                onCancel={handleModalClose} // Reset the form on modal close
                footer={null}
              >
                <Input
                  type="text"
                  name="firstName"
                  label="First Name"
                  value={payload.firstName}
                  placeholder="Enter First Name"
                  onChange={handleInputChange}
                />
                <Input
                  type="text"
                  name="lastName"
                  label="Last Name"
                  value={payload.lastName}
                  placeholder="Enter Last Name"
                  onChange={handleInputChange}
                />
                <div className="select-group">
                  <label
                    htmlFor="role"
                    className="block mb-2 text-grey text-[16px] manrope capitalize"
                  >
                    Role
                  </label>
                  <select
                    id="role"
                    name="role"
                    value={payload.role}
                    onChange={handleInputChange}
                    className="px-6 p-4 mb-3 text-[16px] bg-white w-full border-[1px] duration-300 border-[#d3d3d3] focus:border-orange rounded-md"
                  >
                    <option value="" disabled>
                      Select role
                    </option>
                    {roles?.data?.map(
                      ({ id, name }: { id: string; name: string }) => (
                        <option value={id} key={id}>
                          {name}
                        </option>
                      )
                    )}
                  </select>
                </div>
                <Button
                  className="text-white p-[12px] w-full font-bold rounded-md ease-in-out duration-300 bg-brown"
                  disabled={
                    !payload.firstName || !payload.lastName || !payload.role
                  }
                  isLoading={updatingAdmin}
                  onClick={() => {
                    updateAdmin(payload, {
                      onSuccess: async (data: any) => {
                        message.success(data?.message);
                        handleModalClose();
                        queryClient.invalidateQueries({
                          queryKey: ["useGetAdminsQuery"],
                        });
                      },
                      onError: (error: any) => {
                        message.error(error?.response?.data?.message);
                        handleModalClose();
                      },
                    });
                  }}
                  label={"Proceed"}
                />
              </Modal>
            </div>
          </div>
        )}
        <div className="pl-[20px] text-[12px]">
          <h3 className="font-bold mb-4">Driver Orders</h3>
          <Table
            dataSource={driverOrders?.data?.docs ? driverOrders.data.docs : []}
            columns={orderColumns}
            scroll={{ y: "calc(100vh - 200px)" }}
            rowKey="id"
            className="flex-grow"
            loading={isPendingDriverOrders}
            rowClassName="text-[12px]"
            // onRow={(record: any) => ({
            //   onClick: () => navigate(`/admin-management/user/${record.id}`),
            // })}
          />
        </div>
      </div>
    </DashboardLayout>
  );
};

export default SingleAdminPreview;
